import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Download, Share2, Film, Wand2, X, Copy, Music2, Facebook } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u || "");

// ===== Caption builder TikTok / Réseaux sociaux ===========================
// Transforme un prompt verbeux en caption courte + emojis + hashtags Madagascar.
const HASHTAGS_BANK = {
  baobab:    ["#Baobab", "#AlleeDesBaobabs", "#Morondava"],
  tana:      ["#Antananarivo", "#Tana", "#Madagascar"],
  lemurien:  ["#Lémurien", "#Lemur", "#Wildlife", "#Andasibe"],
  nosybe:    ["#NosyBe", "#Plage", "#OceanIndien"],
  taxibe:    ["#TaxiBe", "#Tana", "#StreetLife"],
  hira:      ["#HiraGasy", "#Tradition", "#Folklore"],
  pirogue:   ["#Pirogue", "#Pêcheurs", "#Lagon"],
  drone:     ["#Drone", "#Aerial", "#NatureLover"],
  riziere:   ["#Rizières", "#Ambositra", "#Hauts plateaux"],
  zebu:      ["#Zébu", "#Tuléar", "#Tradition"],
};
const HASHTAGS_BASE = ["#Madagascar", "#Voamada", "#MadeInMadagascar", "#TikTokMada", "#FierDÊtreMalgache"];

const EMOJI_PREFIX_BANK = ["🇲🇬", "✨", "🎬", "🌍", "🔥", "💫"];

function detectThemes(prompt) {
  const p = prompt.toLowerCase();
  const found = [];
  if (/baobab/.test(p))                          found.push("baobab");
  if (/antananarivo|tana\b/.test(p))             found.push("tana");
  if (/lémurien|lemur|indri|andasibe/.test(p))   found.push("lemurien");
  if (/nosy be|nosybe|plage|lagon/.test(p))      found.push("nosybe");
  if (/taxi-?be/.test(p))                        found.push("taxibe");
  if (/hira ?gasy|tradition/.test(p))            found.push("hira");
  if (/pirogue|pêcheur/.test(p))                 found.push("pirogue");
  if (/drone|aérienn?e|vue du ciel/.test(p))     found.push("drone");
  if (/rizière|ambositra|hauts plateaux/.test(p))found.push("riziere");
  if (/zébu|zebu|tuléar/.test(p))                found.push("zebu");
  return found;
}

function buildSocialCaption(prompt, style) {
  if (!prompt) return "";
  const themes = detectThemes(prompt);
  const emoji = EMOJI_PREFIX_BANK[Math.floor(Math.random() * EMOJI_PREFIX_BANK.length)];
  // Hook accrocheur basé sur le style
  const hooks = {
    cinematic:    "Le Madagascar comme tu ne l'as jamais vu",
    documentary:  "À découvrir absolument",
    streetart:    "Madagascar dans toute sa créativité",
    tiktok_promo: "Tu vas adorer 👀",
    nature:       "La nature malgache, brute et magnifique",
  };
  const hook = hooks[style] || "Made in Madagascar 🇲🇬";

  // Résumé court du prompt (1ère phrase, max 80 chars)
  const firstSentence = prompt.split(/[.!?]/)[0].trim();
  const summary = firstSentence.length > 90 ? firstSentence.slice(0, 90) + "…" : firstSentence;

  // Hashtags : thèmes détectés + base
  const themeTags = themes.flatMap(t => HASHTAGS_BANK[t] || []);
  const allTags = [...new Set([...themeTags, ...HASHTAGS_BASE])].slice(0, 12);

  const cta = "💬 Dis-nous en commentaire ce que ça t'inspire !";
  return `${emoji} ${hook}\n\n${summary}\n\n${cta}\n\n${allTags.join(" ")}`;
}

const SIZES = [
  { v: "1024x1792", label: "📱 Vertical 9:16 (TikTok / Reels)" },
  { v: "1024x1024", label: "🟦 Carré 1:1 (Insta feed)" },
  { v: "1280x720",  label: "📺 Paysage 16:9 (YouTube)" },
  { v: "1792x1024", label: "🎬 Cinéma large" },
];

const STYLES = [
  { v: "",             label: "— Aucun style (prompt brut)" },
  { v: "cinematic",    label: "🎬 Cinématique Sony A7" },
  { v: "documentary",  label: "📺 Documentaire / reportage" },
  { v: "streetart",    label: "🎨 Street-art / graffiti animé" },
  { v: "tiktok_promo", label: "⚡ TikTok promo énergique" },
  { v: "nature",       label: "🌿 Nature Madagascar (drone)" },
];

const QUICK_PROMPTS = [
  "Un taxi-be jaune et blanc roulant dans une rue colorée d'Antananarivo, vendeurs ambulants, ambiance dynamique d'après-midi",
  "Vue aérienne drone de l'Allée des Baobabs au coucher du soleil, charrette à zébu qui passe, ciel orange et violet",
  "Pirogue malgache glissant sur le lagon turquoise de Nosy Be, palmiers, pêcheurs locaux",
  "Concert de hira gasy à Antananarivo, musiciens en costumes traditionnels, public qui danse, soir festif",
  "Forêt d'Andasibe, lémuriens indri qui sautent entre les arbres, brume matinale, lumière dorée",
];

export const AdminVideoStudioPanel = () => {
  const [videos, setVideos]   = useState([]);
  const [prompt, setPrompt]   = useState("");
  const [model, setModel]     = useState("slideshow");
  const [size, setSize]       = useState("1024x1792");
  const [duration, setDuration] = useState(8);
  const [style, setStyle]     = useState("cinematic");
  const [busy, setBusy]       = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [shareVideo, setShareVideo] = useState(null); // {video, caption} ou null

  const load = useCallback(() => {
    api.get("/admin/video-studio/videos")
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Timer pendant la génération
  useEffect(() => {
    if (!busy) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const generate = async () => {
    if (prompt.trim().length < 8) { toast.error("Prompt trop court (8 caractères min)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/video-studio/generate",
        { prompt, model, size, duration, style },
        { timeout: 0 }
      );
      const mb = Math.round((data.bytes || 0) / 1024 / 1024 * 10) / 10;
      toast.success(`🎬 Vidéo générée (${mb} MB) !`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Génération échouée";
      // Si erreur budget Sora → toast long + suggestion
      if (/budget|insufficient/i.test(msg)) {
        toast.error(msg, { duration: 12000, description: "💡 Astuce : sélectionne le modèle Slideshow Nano Banana (gratuit ⭐)" });
      } else {
        toast.error(msg, { duration: 8000 });
      }
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette vidéo ?")) return;
    try { await api.post(`/admin/video-studio/${id}/delete`); load(); toast.success("Supprimée"); }
    catch { toast.error("Suppression échouée"); }
  };

  const cleanupFailed = async () => {
    const failed = videos.filter(v => v.status === "failed");
    if (failed.length === 0) { toast.info("Aucun échec à nettoyer"); return; }
    if (!confirm(`Supprimer ${failed.length} vidéo(s) en échec ?`)) return;
    try {
      await Promise.all(failed.map(v => api.post(`/admin/video-studio/${v.video_id}/delete`)));
      load();
      toast.success(`${failed.length} échec(s) nettoyé(s) ✨`);
    } catch { toast.error("Nettoyage partiel — recharge la page"); }
  };

  const openShare = (v) => {
    setShareVideo({ video: v, caption: buildSocialCaption(v.prompt, v.style) });
  };

  return (
    <div className="space-y-6" data-testid="admin-video-studio-panel">
      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-500/10 via-cyan-500/5 to-amber-500/10 border border-white/10 p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="text-3xl">🎬</div>
          <div>
            <h2 className="text-2xl font-black tracking-tight">Vidéo IA — Sora 2</h2>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">
              Décris une scène en français, Sora 2 génère une vidéo 4-12s. Prêt à poster sur TikTok / Reels.
              <br /><span className="text-amber-300/80">⏱️ Génération : 2 à 5 minutes par vidéo. Reste sur la page.</span>
            </p>
          </div>
        </div>

        {/* Prompts rapides */}
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">💡 Idées de prompts</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((q, i) => (
              <button
                key={i}
                onClick={() => setPrompt(q)}
                data-testid={`video-quick-${i}`}
                className="text-[11px] px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-cyan-500/20 border border-white/10 text-white/70 hover:text-white max-w-[260px] truncate"
                title={q}
              >
                {q.slice(0, 50)}...
              </button>
            ))}
          </div>
        </div>

        {/* Prompt */}
        <div className="mb-4">
          <label className="text-xs uppercase tracking-wider text-white/50">Prompt vidéo (français)</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            data-testid="video-prompt"
            placeholder="Ex: Vue drone d'un taxi-be jaune dans une rue colorée d'Antananarivo, palmiers, lumière dorée..."
            rows={4}
            maxLength={2000}
            className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400/60 resize-none"
          />
          <div className="text-[10px] text-white/40 text-right mt-0.5">{prompt.length} / 2000</div>
        </div>

        {/* Params */}
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Modèle</label>
            <select value={model} onChange={e => setModel(e.target.value)} data-testid="video-model"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
              <option value="slideshow">🎨 Slideshow Nano Banana — GRATUIT ⭐</option>
              <option value="sora-2">🎬 Sora 2 (~$0.10/vidéo, nécessite crédit Emergent)</option>
              <option value="sora-2-pro">💎 Sora 2 Pro (~$0.30/vidéo, nécessite crédit Emergent)</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Format</label>
            <select value={size} onChange={e => setSize(e.target.value)} data-testid="video-size"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
              {SIZES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Durée</label>
            <select value={duration} onChange={e => setDuration(parseInt(e.target.value))} data-testid="video-duration"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
              <option value={4}>4 secondes (rapide)</option>
              <option value={8}>8 secondes (recommandé)</option>
              <option value={12}>12 secondes (max)</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Style</label>
            <select value={style} onChange={e => setStyle(e.target.value)} data-testid="video-style"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
              {STYLES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <button
          onClick={generate}
          disabled={busy}
          data-testid="video-generate"
          className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:from-fuchsia-400 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed font-bold flex items-center justify-center gap-2 shadow-lg shadow-fuchsia-500/20"
        >
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
          {busy ? `Génération en cours... ${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}` : "🎬 Générer la vidéo"}
        </button>
        {busy && (
          <div className="mt-3 text-xs text-amber-200/80 text-center">
            {model === "slideshow"
              ? "⏳ Slideshow gratuit : génération de 4 images Nano Banana + assemblage FFmpeg (~1-2 min)."
              : "⏳ Sora 2 prend 2 à 5 minutes. Ne ferme pas l'onglet."}
          </div>
        )}
      </div>

      {/* Galerie */}
      {videos.length === 0 ? (
        <div className="text-center py-16 text-white/40 border border-dashed border-white/10 rounded-2xl">
          <Film size={48} className="mx-auto mb-3 opacity-50" />
          <p>Aucune vidéo encore.</p>
          <p className="text-sm">Génère ta première vidéo Sora 2 ci-dessus ✨</p>
        </div>
      ) : (
        <div className="space-y-3">
          {videos.some(v => v.status === "failed") && (
            <div className="flex items-center justify-between bg-red-500/5 border border-red-400/20 rounded-xl px-3 py-2 text-xs">
              <span className="text-red-200">⚠ {videos.filter(v => v.status === "failed").length} vidéo(s) en échec</span>
              <button onClick={cleanupFailed} data-testid="cleanup-failed"
                className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-100 font-semibold">
                🧹 Nettoyer les échecs
              </button>
            </div>
          )}
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="video-grid">
          {videos.map(v => (
            <div key={v.video_id} className="bg-white/[0.03] border border-white/10 rounded-2xl p-3" data-testid={`video-card-${v.video_id}`}>
              {v.status === "ready" && v.video_url ? (
                <video src={absUrl(v.video_url)} controls preload="metadata"
                  className="w-full aspect-[9/16] object-cover rounded-xl bg-black" />
              ) : v.status === "failed" ? (
                <div className="aspect-[9/16] rounded-xl bg-red-500/10 border border-red-400/30 flex items-center justify-center p-4 text-red-300 text-xs">
                  ⚠ Échec : {(v.error || "inconnu").slice(0, 100)}
                </div>
              ) : (
                <div className="aspect-[9/16] rounded-xl bg-white/5 flex flex-col items-center justify-center text-white/40 p-3 text-center">
                  <Loader2 className="animate-spin mb-2" size={24} />
                  <div className="text-xs">En attente...</div>
                  {v.created_at && (
                    <div className="text-[10px] text-white/30 mt-1">
                      Depuis {Math.max(0, Math.floor((Date.now() - new Date(v.created_at + (v.created_at.includes('Z') ? '' : 'Z')).getTime()) / 60000))} min
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-white/80 line-clamp-2 mt-2">{v.prompt}</p>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-white/40">
                <span>{v.model}</span> · <span>{v.size}</span> · <span>{v.duration}s</span>
                {v.size_bytes > 0 && <span>· {Math.round(v.size_bytes/1024/1024*10)/10} MB</span>}
              </div>
              {v.status === "ready" && v.video_url && (
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  <a href={absUrl(v.video_url)} download className="px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] flex items-center justify-center gap-1">
                    <Download size={11} /> MP4
                  </a>
                  <button onClick={() => openShare(v)} data-testid={`video-share-${v.video_id}`} className="px-2 py-1.5 rounded-lg bg-gradient-to-r from-pink-500/15 to-cyan-500/15 hover:from-pink-500/30 hover:to-cyan-500/30 text-pink-200 text-[11px] flex items-center justify-center gap-1">
                    <Share2 size={11} /> Partager
                  </button>
                  <button onClick={() => remove(v.video_id)} className="px-2 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-[11px] flex items-center justify-center gap-1">
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
              {v.status === "failed" && (
                <button onClick={() => remove(v.video_id)} data-testid={`video-delete-${v.video_id}`} className="mt-2 w-full px-2 py-1 rounded-lg bg-red-500/10 text-red-300 text-[11px]">
                  Supprimer
                </button>
              )}
              {v.status === "queued" && (
                <button onClick={() => remove(v.video_id)} data-testid={`video-cancel-${v.video_id}`} className="mt-2 w-full px-2 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-[11px]">
                  Annuler
                </button>
              )}
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Modal de partage social */}
      {shareVideo && (
        <SharePreviewModal
          video={shareVideo.video}
          initialCaption={shareVideo.caption}
          onClose={() => setShareVideo(null)}
        />
      )}
    </div>
  );
};

// ===== Modal de prévisualisation + partage TikTok / Facebook / Instagram ===
const SharePreviewModal = ({ video, initialCaption, onClose }) => {
  const [caption, setCaption] = useState(initialCaption);
  const [busy, setBusy] = useState(false);
  const url = absUrl(video.video_url);

  const fetchBlob = async () => {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Vidéo inaccessible");
    return await res.blob();
  };

  const shareNative = async (network) => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], `voamada-${video.video_id}.mp4`, { type: "video/mp4" });

      // Mobile : Web Share API natif (l'utilisateur choisit l'app dans la feuille système)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: caption, title: "Voamada Studio IA" });
          toast.success("Partage natif ouvert 🚀");
          return;
        } catch (e) {
          if (e.name === "AbortError") return; // user annulé
        }
      }

      // Fallback desktop : DL + clipboard + ouvrir l'uploader
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `voamada-${video.video_id}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);

      try { await navigator.clipboard.writeText(caption); } catch { /* clipboard non disponible */ }

      const targets = {
        tiktok:    "https://www.tiktok.com/upload",
        facebook:  "https://www.facebook.com/",
        instagram: "https://www.instagram.com/",
      };
      window.open(targets[network] || targets.tiktok, "_blank");

      const labels = { tiktok: "TikTok", facebook: "Facebook", instagram: "Instagram" };
      toast.success(`MP4 téléchargé + caption copiée — ${labels[network]} ouvert`);
    } catch (e) {
      toast.error("Partage échoué : " + (e.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copiée dans le presse-papier ✂️");
    } catch {
      toast.error("Copie échouée");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-zinc-900 border border-white/15 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-zinc-900 border-b border-white/10 p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Share2 size={18} className="text-pink-400" /> Partager sur les réseaux
          </h3>
          <button onClick={onClose} data-testid="share-modal-close" className="p-1.5 rounded-lg hover:bg-white/10">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Preview vidéo */}
          <div className="grid sm:grid-cols-[180px,1fr] gap-4">
            <video src={url} controls preload="metadata" className="w-full aspect-[9/16] object-cover rounded-xl bg-black" />
            <div className="space-y-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-2">
                  Caption optimisée
                  <button onClick={() => setCaption(buildSocialCaption(video.prompt, video.style))}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 hover:bg-cyan-500/30 text-cyan-200" data-testid="share-regen-caption">
                    🔄 Re-générer
                  </button>
                </label>
                <textarea
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  data-testid="share-caption"
                  rows={9}
                  maxLength={2200}
                  className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-xs focus:outline-none focus:border-pink-400/60 resize-none font-mono"
                />
                <div className="text-[10px] text-white/40 text-right">{caption.length} / 2200</div>
              </div>
            </div>
          </div>

          {/* Boutons réseaux */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button onClick={() => shareNative("tiktok")} disabled={busy} data-testid="share-tiktok"
              className="px-3 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-cyan-500 hover:from-pink-400 hover:to-cyan-400 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2">
              <Music2 size={14} /> TikTok
            </button>
            <button onClick={() => shareNative("facebook")} disabled={busy} data-testid="share-facebook"
              className="px-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2">
              <Facebook size={14} /> Facebook
            </button>
            <button onClick={() => shareNative("instagram")} disabled={busy} data-testid="share-instagram"
              className="px-3 py-2.5 rounded-xl bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600 hover:opacity-90 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2">
              📸 Instagram
            </button>
            <button onClick={copyCaption} disabled={busy} data-testid="share-copy"
              className="px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2">
              <Copy size={14} /> Copier
            </button>
          </div>

          <div className="text-[11px] text-white/50 bg-white/5 rounded-lg p-2.5 border border-white/10">
            💡 <b>Mobile</b> : la feuille de partage iOS/Android s&apos;ouvre avec la vidéo prête à publier dans l&apos;app TikTok / FB / Insta.<br/>
            💡 <b>Desktop</b> : le MP4 est téléchargé, la caption copiée, et l&apos;uploader du réseau s&apos;ouvre dans un nouvel onglet.
          </div>
        </div>
      </div>
    </div>
  );
};
